/**
 * Client-side GitHub helpers.
 * Push logic lives in the push-to-github edge function (Git Trees API, atomic commit).
 * This file only has client-side utilities: token verification and file filtering.
 */

const GITHUB_API = "https://api.github.com";

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
  };
}

export async function verifyToken(token: string): Promise<{ login: string } | { error: string }> {
  const res = await fetch(`${GITHUB_API}/user`, { headers: authHeaders(token) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    return { error: (body as { message?: string }).message || `HTTP ${res.status}` };
  }
  const data = (await res.json()) as { login?: string };
  return { login: data.login || "" };
}

const MAX_FILE_BYTES = 800 * 1024;

export type SkippedFile = { path: string; reason: string };

export function filterFilesForPush(
  files: Array<{ name: string; content: string }>
): { toPush: Array<{ name: string; content: string }>; skipped: SkippedFile[] } {
  const toPush: Array<{ name: string; content: string }> = [];
  const skipped: SkippedFile[] = [];
  for (const f of files) {
    if (f.name.includes("node_modules")) {
      skipped.push({ path: f.name, reason: "node_modules" });
      continue;
    }
    const bytes = new TextEncoder().encode(f.content).length;
    if (bytes > MAX_FILE_BYTES) {
      skipped.push({ path: f.name, reason: `>800kb (${Math.round(bytes / 1024)}kb)` });
      continue;
    }
    toPush.push(f);
  }
  return { toPush, skipped };
}
